import { useCallback, useEffect, useRef } from 'react';
import type { EntryPointActions } from '@atlassian/jira-entry-point/src/controllers/utils/types.tsx';
import { getHoverProxy, addListeners, removeListeners } from './utils';

export type EntryPointToggleTrigger = (element: HTMLInputElement | null) => void;

export const useEntryPointToggleTrigger = (
	actions: EntryPointActions,
	offValue = false,
): EntryPointToggleTrigger => {
	const removePreviousListeners = useRef<(() => void) | null>(null);
	const isMounted = useRef<boolean>(true);

	useEffect(
		() => () => {
			removePreviousListeners.current?.();
			removePreviousListeners.current = null;
			isMounted.current = false;
		},
		[],
	);

	return useCallback(
		(refElement: HTMLInputElement | null) => {
			if (!isMounted.current) {
				return;
			}

			removePreviousListeners.current?.();

			if (refElement) {
				const { type } = refElement;

				if (type !== 'checkbox' && type !== 'radio') {
					throw new Error(
						`HTMLInputElement ref of type ${type} cannot be used with useEntryPointToggleTrigger. Try checkbox or radio.`,
					);
				}

				const hoverElement = getHoverProxy(refElement);
				const handlers = {
					load() {
						if (refElement.checked !== offValue) {
							actions.load();
						}
					},
					preload() {
						if (refElement.checked === offValue) {
							actions.preload();
						}
					},
					cancelPreload() {
						if (refElement.checked === offValue) {
							actions.cancelPreload();
						}
					},
				};

				addListeners(handlers, refElement, hoverElement);

				removePreviousListeners.current = () => {
					removeListeners(handlers, refElement, hoverElement);
				};
			} else {
				removePreviousListeners.current = null;
			}
		},
		[actions, offValue],
	);
};
